import {
  Button,
  Card,
  Col,
  Form,
  Input,
  message,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
} from 'antd';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import api from '../config/api';
import { EMAIL_TEMPLATE_CODES, type EmailTemplateCode } from '../config/emailTemplateCodes';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// A kódok listája a `config/emailTemplateCodes.ts`-ben él; a `PASSWORD_RESET`
// szándékosan hiányzik belőle (#109).
type Code = EmailTemplateCode;
const CODES: Code[] = EMAIL_TEMPLATE_CODES;

interface EmailTemplate {
  id: string;
  code: Code;
  subject: string;
  body: string;
  cc?: string;
  enabled: boolean;
}

const EmailTemplates: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [code, setCode] = useState<Code>('REGISTRATION_CONFIRMED');
  const [tpl, setTpl] = useState<EmailTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (!id) return;
    loadTemplate();
  }, [id, code]);

  const loadTemplate = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/events/${id}/email-templates/${code}`);
      setTpl(r.data);
      form.setFieldsValue(r.data);
    } catch {
      setTpl(null);
      form.resetFields();
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (vals: any) => {
    try {
      await api.put(`/events/${id}/email-templates/${code}`, vals);
      message.success(t('emailTemplates.saved'));
      await loadTemplate();
    } catch (e: any) {
      message.error(e.response?.data?.message ?? t('emailTemplates.failed'));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/events/${id}/email-templates/${code}`);
      message.success(t('emailTemplates.overrideRemoved'));
      await loadTemplate();
    } catch {
      message.error(t('emailTemplates.failed'));
    }
  };

  return (
    <Card>
      <Title level={3}>{t('emailTemplates.title')}</Title>
      {/*
        A bekezdes beagyazott elemeket tartalmaz, ezert Trans kell hozza. A
        szamozott helyek (<0/> …) a `components` TOMB elemeire hivatkoznak. Tombot
        hasznalunk, nem gyermekeket: a JSX-ben a sorok kozotti sortorések szoveg-
        csomopontkent szamitanak, es elcsusztatjak az indexeket — merve, a peldak
        uresen jottek ki.
        igy a placeholder-peldak NEM kerulnek bele a forditando szovegbe — a
        `{{ key }}` alaku mintak kulonben utkoznenek az i18next interpolaciojaval.
      */}
      <Paragraph>
        <Trans
          i18nKey="emailTemplates.intro"
          components={[
            <Tag key="key">{`{{ key }}`}</Tag>,
            <code key="keresztnev">{`{{ keresztnev }}`}</code>,
            <code key="event_name">{`{{ event_name }}`}</code>,
            <code key="price">{`{{ price }}`}</code>,
            <code key="payment_deadline">{`{{ payment_deadline }}`}</code>,
          ]}
        />
      </Paragraph>
      <Row gutter={16}>
        <Col span={6}>
          <Select
            value={code}
            onChange={(v) => setCode(v)}
            options={CODES.map((c) => ({ label: c, value: c }))}
            style={{ width: '100%' }}
          />
        </Col>
      </Row>
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: 16 }}
        initialValues={{ enabled: true }}
        onFinish={onSubmit}
      >
        <Form.Item
          name="subject"
          label={t('emailTemplates.subject')}
          rules={[{ required: true, message: t('emailTemplates.subjectRequired') }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="body"
          label={t('emailTemplates.body')}
          rules={[{ required: true, message: t('emailTemplates.bodyRequired') }]}
        >
          <TextArea rows={14} />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="cc" label={t('emailTemplates.cc')}>
              <Input placeholder={t('emailTemplates.ccPlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="enabled" label={t('emailTemplates.enabled')} valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            {t('emailTemplates.saveOverride')}
          </Button>
          {tpl && (
            <Button danger onClick={remove}>
              {t('emailTemplates.removeOverride')}
            </Button>
          )}
          <Text type="secondary">
            {tpl ? t('emailTemplates.overrideActive') : t('emailTemplates.noOverride')}
          </Text>
        </Space>
      </Form>
    </Card>
  );
};

export default EmailTemplates;
